import { useState, Suspense } from 'react'
import { Outlet, Link, useLocation } from 'react-router-dom'
import PageLoadingSpinner from './PageLoadingSpinner'
import Logo from './Logo'
import {
  LayoutDashboard, Building2, Wrench, Shield, PoundSterling,
  CheckSquare, Menu, X, LogOut
} from 'lucide-react'
import { useAuth } from '../hooks/useAuth'
import { useProfile } from '../hooks/useProfile'
import NotificationsDropdown from './NotificationsDropdown'

const NAV_ITEMS = [
  { to: '/client', icon: LayoutDashboard, label: 'Overview' },
  { to: '/client/properties', icon: Building2, label: 'Properties' },
  { to: '/client/issues', icon: Wrench, label: 'Maintenance' },
  { to: '/client/compliance', icon: Shield, label: 'Compliance' },
  { to: '/client/financials', icon: PoundSterling, label: 'Financials' },
  { to: '/client/approvals', icon: CheckSquare, label: 'Approvals' },
]

export default function LandlordClientLayout() {
  const { signOut } = useAuth()
  const { profile } = useProfile()
  const location = useLocation()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--bg-main)' }}>
      {/* Sidebar - desktop */}
      <aside style={{
        width: 220,
        background: 'var(--bg-card)',
        borderRight: '1px solid var(--border-color)',
        padding: '20px 0',
      }} className="hidden md:flex flex-col">
        {/* Logo */}
        <div style={{ padding: '0 16px 20px', borderBottom: '1px solid var(--border-color)', marginBottom: 8 }}>
          <Logo size="sm" variant="dark" />
          <p style={{ fontSize: 11, color: 'var(--text-tertiary)', margin: '4px 0 0' }}>
            Client Portal
          </p>
        </div>

        {/* Nav */}
        <nav style={{ flex: 1, padding: '8px 8px' }}>
          {NAV_ITEMS.map(item => {
            const active = location.pathname === item.to ||
              (item.to !== '/client' && location.pathname.startsWith(item.to))
            return (
              <Link
                key={item.to}
                to={item.to}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '10px 12px', borderRadius: 8,
                  fontSize: 13, fontWeight: active ? 600 : 500,
                  color: active ? 'var(--color-accent)' : 'var(--text-secondary)',
                  background: active ? 'var(--color-accent-muted)' : 'transparent',
                  textDecoration: 'none',
                  marginBottom: 2,
                  transition: 'all 0.15s',
                }}
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </Link>
            )
          })}
        </nav>

        {/* User */}
        <div style={{ padding: '12px 16px', borderTop: '1px solid var(--border-color)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
              {profile?.full_name || 'Client'}
            </div>
            <NotificationsDropdown variant="light" />
          </div>
          <button
            onClick={() => signOut()}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              fontSize: 12, color: 'var(--text-tertiary)',
              background: 'none', border: 'none', cursor: 'pointer',
              padding: '4px 0', marginTop: 4,
            }}
          >
            <LogOut className="w-3.5 h-3.5" />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Mobile header */}
      <div className="md:hidden flex justify-between items-center" style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 40,
        background: 'var(--bg-card)', borderBottom: '1px solid var(--border-color)',
        padding: '12px 16px',
      }}>
        <Logo size="sm" variant="dark" />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <NotificationsDropdown variant="light" />
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
          >
            {mobileMenuOpen ? (
              <X className="w-5 h-5" style={{ color: 'var(--text-primary)' }} />
            ) : (
              <Menu className="w-5 h-5" style={{ color: 'var(--text-primary)' }} />
            )}
          </button>
        </div>
      </div>

      {/* Mobile menu overlay */}
      {mobileMenuOpen && (
        <div className="md:hidden" style={{
          position: 'fixed', inset: 0, zIndex: 35,
          background: 'rgba(0,0,0,0.4)',
        }} onClick={() => setMobileMenuOpen(false)}>
          <nav style={{
            position: 'absolute', top: 52, left: 0, right: 0,
            background: 'var(--bg-card)',
            borderBottom: '1px solid var(--border-color)',
            padding: 12,
          }} onClick={e => e.stopPropagation()}>
            {NAV_ITEMS.map(item => {
              const active = location.pathname === item.to
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={() => setMobileMenuOpen(false)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '10px 12px', borderRadius: 8,
                    fontSize: 14, fontWeight: active ? 600 : 500,
                    color: active ? 'var(--color-accent)' : 'var(--text-primary)',
                    background: active ? 'var(--color-accent-muted)' : 'transparent',
                    textDecoration: 'none',
                  }}
                >
                  <item.icon className="w-4 h-4" />
                  {item.label}
                </Link>
              )
            })}
          </nav>
        </div>
      )}

      {/* Main content */}
      <main style={{ flex: 1 }} className="pt-16 md:pt-6 px-6 pb-6 max-w-[1400px] mx-auto w-full 2xl:!max-w-[1600px]">
        <Suspense fallback={<PageLoadingSpinner />}>
          <Outlet />
        </Suspense>
      </main>
    </div>
  )
}
